import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import {
  getChildById,
  getChildren,
  createChild,
  updateChild,
  deleteChild,
} from './studentApi';
import { toast } from 'react-toastify';

/**
 * Hook for fetching list of students with proper data transformation
 */
export const useGetChildren = () => {
  return useQuery({ 
    queryKey: ['children'], 
    queryFn: getChildren,
    onError: (error) => {
      toast.error('Failed to fetch students: ' + (error.message || 'Unknown error'));
    }
  });
};

/**
 * Enhanced hook for fetching student details by ID with robust error handling
 * and data normalization
 */
export const useGetChildById = (childId) => {
  const location = useLocation();
  // Check for preloaded data from navigation state
  const preloadedData = location.state?.studentData;

  return useQuery({
    queryKey: ['children', childId],
    queryFn: async () => {
      // Validate studentId to prevent ObjectID errors
      if (!childId) {
        throw new Error('Student ID is required');
      }
      
      try {
        // Get student data from API
        const studentData = await getChildById(childId);
        console.log('API Response in Query:', studentData);

        if (!studentData) {
          throw new Error('Student data not found');
        }

        // Normalize and transform the data with null safety
        const mappedData = {
          id: studentData.childId || studentData.id || childId,
          childId: studentData.childId || studentData.id || childId,
          name: `${studentData.firstName || ''} ${studentData.lastName || ''}`.trim() || 'Unnamed Student',
          firstName: studentData.firstName || '',
          lastName: studentData.lastName || '',
          photoUrl: studentData.photoUrl || "/placeholder.svg",
          avatar: studentData.photoUrl || "/placeholder.svg",
          parentId: studentData.parentId || studentData.parent?.id || null,
          organizationId: studentData.organizationId || null,
          status: studentData.status || 'inactive',
          isActive: studentData.status === 'active',
          className: studentData.class || 'Not Assigned',
          class: studentData.class || 'Not Assigned',
          attendance: studentData.attendance || [],
          performance: studentData.performance || 0,
          teacher: studentData.teacher || { name: 'Not Assigned' },
          parent: studentData.parent || null,
          createdAt: studentData.createdAt || null,
          updatedAt: studentData.updatedAt || null
        };

        console.log('Mapped Student Data:', mappedData);
        return mappedData;
      } catch (error) {
        console.error(`Error fetching student with ID ${childId}:`, error);
        throw error;
      }
    },
    // Only run the query if we have a valid childId
    enabled: Boolean(childId),
    // Use preloaded data if available
    initialData: preloadedData,
    // Configure stale time and cache behavior
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry if the error is due to missing ID
      if (error.message?.includes('ID is required') || error.message?.includes('ObjectID')) {
        return false;
      }
      // Otherwise retry twice
      return failureCount < 2;
    },
    onError: (error) => {
      toast.error('Failed to fetch student details: ' + (error.message || 'Unknown error'));
    }
  });
};

export const useCreateChild = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['create_child'],
    mutationFn: createChild,
    onSuccess: () => {
      queryClient.invalidateQueries(['children']);
      toast.success('Student added successfully');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || error.message || 'Error creating student';
      console.error('Create child error:', errorMessage);
      toast.error(errorMessage);
    },
  });
};

export const useUpdateChild = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['update_child'],
    mutationFn: ({ id, data }) => updateChild(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['children']);
      toast.success('Student updated successfully');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || error.message || 'Error updating student';
      console.error('Update child error:', errorMessage);
      toast.error(errorMessage);
    },
  });
};

export const useDeleteChild = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['delete_child'],
    mutationFn: deleteChild,
    onSuccess: () => {
      queryClient.invalidateQueries(['children']);
      toast.success('Student deleted successfully');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || error.message || 'Error deleting student';
      console.error('Delete child error:', errorMessage);
      toast.error(errorMessage);
    },
  });
};
